// TypeScript mirror of the backend AI-trace schema
// (src/inrange_agents/ai_trace.py). One definition consumed by the web app and
// by the AiTraceViewer component, so every AI surface renders identically.
// Ported from BMA's building-manager-api-client models/aiTrace.ts.

export type AITraceEntryType =
  | "system_prompt"
  | "user_prompt"
  | "thinking"
  | "text"
  | "tool_use"
  | "tool_result"
  | "tool_model_call"
  | "tool_catalogue"
  // The deterministic routing decision (why this agent was chosen), recorded as
  // the first entry since the rule-based router makes no AI call of its own.
  | "routing"
  // Pacing guidance injected after a tool batch ("you may call tools in N more
  // turn(s)…") so the trace shows what the agent was told about its budget.
  | "turn_budget"
  // Token-budget guidance injected alongside the turn budget ("~X of ~Y tokens
  // used, ~Z free…") - distinct from turn_budget so the viewer labels it right.
  | "context_budget"
  // A compact summary of the earlier-turn messages carried into this turn.
  | "conversation_history"
  // A turn that raised before it could answer: the reply it is replayed with, then
  // the error. Mirrors ai_trace.TURN_FAILED.
  | "turn_failed"
  // A part boundary in a merged trace (load_traces_merged): several persisted
  // trace parts folded into one /costs row, each separated by this marker. Carries
  // a label + the part's token subtotal + start offset; rendered as a separator,
  // not an expandable step.
  | "trace_part_divider";

// The four token classes for a single model turn / internal sub-call.
// Lands on the first entry of a model turn (and on a tool_model_call entry) so
// the viewer can show which step of an interaction cost the most.
export interface AITraceUsage {
  input_tokens?: number;
  output_tokens?: number;
  cache_read_input_tokens?: number;
  cache_creation_input_tokens?: number;
}

// A named, independently-collapsible region within one entry's content. Recursive, so
// the viewer can collapse the noisy interior of a large entry (a file read, the system
// prompt's worked-examples block) without scrolling past everything else.
export interface AITraceSection {
  label: string;
  content?: string | null;
  sections?: AITraceSection[];
  lineCount?: number;
  truncated?: boolean;
  defaultCollapsed?: boolean;
}

export interface AITraceEntry {
  role?: string; // system | user | assistant | tool
  type?: AITraceEntryType | string;
  // Which agent's loop produced this step ("Manager", "Grid", "Grid subagent").
  // One trace can hold several agents' steps - a delegated run nests under the
  // tool call that made it - and the model id can't tell them apart (both fleets
  // run the same model). Absent on traces persisted before this field existed
  // and on single-shot builders; the viewer then renders no chip.
  agent?: string | null;
  label?: string | null;
  content?: string | null; // flat text (when no nested sections)
  sections?: AITraceSection[] | null; // structured, nested-collapsible content
  name?: string | null; // tool name (tool_use / tool_result)
  // The document a file tool acted on (filename / "email subject: …"), so the
  // viewer names the file a tool_use / tool_result touched, not just the tool.
  target?: string | null;
  input?: Record<string, unknown> | null; // tool input (tool_use)
  atSeconds?: number;
  modelSeconds?: number | null;
  toolSeconds?: number | null;
  usage?: AITraceUsage | null; // per-entry token usage (first entry of a turn)
  // The model that served this turn (after any throttle fallback), on the first
  // entry of the turn alongside `usage`. Lets the viewer show per-stage model and
  // makes per-stage cost priceable when a run spans models (map+reduce, fallback).
  model_id?: string | null;
  isError?: boolean;
  // A problem read the loop classified (raised / blank / garbled / truncated):
  // the reason + operator-facing detail, so the viewer flags + explains it.
  issueReason?: string | null;
  issueDetail?: string | null;
  // The complete trace of a sub-agent run this step delegated (only on a
  // `tool_model_call` recording a delegate / spawn_subagent call). Nested rather
  // than spliced into the parent's entry list, so the parent's timeline and
  // header totals are untouched - the sub-run's spend is already this entry's
  // `usage`, and its `atSeconds` run on their own 0-based clock.
  nestedTrace?: AITrace | null;
  // On a run trace's turn divider: the project that turn worked (null for none).
  project_id?: string | null;
}

// A trace is stored and rendered WHOLE - it is never truncated, and there is no
// separate capped "display copy" (we never want to lose data from an AI trace).
export interface AITrace {
  entries: AITraceEntry[];
  model_id?: string;
  stop_reason?: string;
  usage?: Record<string, number>;
  totalSeconds?: number;
  /** Wall-clock epoch seconds when recording began. Orders whole traces against
   *  each other; atSeconds/totalSeconds are monotonic and only order entries
   *  within one. Absent on traces written before it existed. */
  startedAt?: number;
}

// True when a trace has something to render.
export const hasAITrace = (trace?: AITrace | null): trace is AITrace =>
  !!trace && Array.isArray(trace.entries) && trace.entries.length > 0;
